import { WrappedAppActions } from 'records/AppState';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { ViewMapLayers } from 'records/interactions/ViewMapLayers';
import { ViewMapPunchList } from 'records/interactions/ViewMapPunchList';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { ViewRFIList } from 'records/interactions/ViewRFIList';
import { Interpreter, Machine, State, StateMachine } from 'xstate';

export enum MapOverlayMachineStatesEnum {
  idle = 'idle',
  viewingMapLayersOverlay = 'viewingMapLayersOverlay',
  viewingRFIListOverlay = 'viewingRFIListOverlay',
  viewingRFIOverlay = 'viewingRFIOverlay',
  viewingUpdatePunchOverlay = 'viewingUpdatePunchOverlay',
  viewingMapPunchListOverlay = 'viewingMapPunchListOverlay',
  viewingCreatePunchOverlay = 'viewingCreatePunchOverlay',
}

export enum MapOverlayMachineEventsEnum {
  OPEN_MAP_LAYERS_OVERLAY = 'OPEN_MAP_LAYERS_OVERLAY',
  OPEN_RFI_OVERLAY = 'OPEN_RFI_OVERLAY',
  OPEN_RFI_LIST_OVERLAY = 'OPEN_RFI_LIST_OVERLAY',
  OPEN_UPDATE_PUNCH_OVERLAY = 'OPEN_UPDATE_PUNCH_OVERLAY',
  OPEN_MAP_PUNCH_LIST_OVERLAY = 'OPEN_MAP_PUNCH_LIST_OVERLAY',
  OPEN_CREATE_PUNCH_OVERLAY = 'OPEN_CREATE_PUNCH_OVERLAY',
  CLOSE_OVERLAY = 'CLOSE_OVERLAY',
}

enum Actions {
  VIEW_OVERLAY = 'VIEW_OVERLAY',
}

export type MapOverlayStateMachine = StateMachine<
  MapOverlayMachineContext,
  MapOverlayMachineStates,
  MapOverlayMachineEvents
>;

export type MapOverlayMachineState = State<
  MapOverlayMachineContext,
  MapOverlayMachineEvents,
  MapOverlayMachineStates
>;

export type MapOverlayMachineService = Interpreter<
  MapOverlayMachineContext,
  MapOverlayMachineStates,
  MapOverlayMachineEvents
>;

type MapOverlayMachineContext = Record<string, never>;

type MapOverlayMachineProps = {
  actions: WrappedAppActions;
};

type MapOverlayMachineStates = {
  states: {
    [key in MapOverlayMachineStatesEnum]: Record<string, unknown>;
  };
};

type MapOverlayMachineEvents =
  | {
      type: MapOverlayMachineEventsEnum.OPEN_MAP_LAYERS_OVERLAY;
      data: {
        interaction: ViewMapLayers;
      };
    }
  | {
      type: MapOverlayMachineEventsEnum.OPEN_RFI_OVERLAY;
      data: {
        interaction: ViewRFI;
      };
    }
  | {
      type: MapOverlayMachineEventsEnum.OPEN_RFI_LIST_OVERLAY;
      data: {
        interaction: ViewRFIList;
      };
    }
  | {
      type: MapOverlayMachineEventsEnum.OPEN_UPDATE_PUNCH_OVERLAY;
      data: {
        interaction: UpdatePunch;
      };
    }
  | {
      type: MapOverlayMachineEventsEnum.OPEN_MAP_PUNCH_LIST_OVERLAY;
      data: {
        interaction: ViewMapPunchList;
      };
    }
  | {
      type: MapOverlayMachineEventsEnum.OPEN_CREATE_PUNCH_OVERLAY;
      data: {
        interaction: CreatePunch;
      };
    }
  | {
      type: MapOverlayMachineEventsEnum.CLOSE_OVERLAY;
      data: undefined;
    };

const createMapOverlayMachine = ({
  actions,
}: MapOverlayMachineProps): MapOverlayStateMachine =>
  Machine<
    MapOverlayMachineContext,
    MapOverlayMachineStates,
    MapOverlayMachineEvents
  >(
    {
      id: 'map-overlay-machine',
      initial: MapOverlayMachineStatesEnum.idle,
      on: {
        [MapOverlayMachineEventsEnum.OPEN_MAP_LAYERS_OVERLAY]:
          MapOverlayMachineStatesEnum.viewingMapLayersOverlay,
        [MapOverlayMachineEventsEnum.OPEN_RFI_OVERLAY]:
          MapOverlayMachineStatesEnum.viewingRFIOverlay,
        [MapOverlayMachineEventsEnum.OPEN_RFI_LIST_OVERLAY]:
          MapOverlayMachineStatesEnum.viewingRFIListOverlay,
        [MapOverlayMachineEventsEnum.OPEN_UPDATE_PUNCH_OVERLAY]:
          MapOverlayMachineStatesEnum.viewingUpdatePunchOverlay,
        [MapOverlayMachineEventsEnum.OPEN_MAP_PUNCH_LIST_OVERLAY]:
          MapOverlayMachineStatesEnum.viewingMapPunchListOverlay,
        [MapOverlayMachineEventsEnum.OPEN_CREATE_PUNCH_OVERLAY]:
          MapOverlayMachineStatesEnum.viewingCreatePunchOverlay,
        [MapOverlayMachineEventsEnum.CLOSE_OVERLAY]:
          MapOverlayMachineStatesEnum.idle,
      },
      states: {
        idle: {},
        viewingMapLayersOverlay: {
          entry: Actions.VIEW_OVERLAY,
          exit: () => {
            actions.deleteInteractions('ViewMapLayers');
          },
        },
        viewingRFIOverlay: {
          entry: Actions.VIEW_OVERLAY,
          exit: () => {
            actions.deleteInteractions('ViewRFI');
          },
        },
        viewingRFIListOverlay: {
          entry: Actions.VIEW_OVERLAY,
          exit: () => {
            actions.deleteInteractions('ViewRFIList');
          },
        },
        viewingUpdatePunchOverlay: {
          entry: Actions.VIEW_OVERLAY,
          exit: () => {
            actions.deleteInteractions('UpdatePunch');
          },
        },
        viewingMapPunchListOverlay: {
          entry: Actions.VIEW_OVERLAY,
          exit: () => {
            actions.deleteInteractions('ViewMapPunchList');
          },
        },
        viewingCreatePunchOverlay: {
          entry: Actions.VIEW_OVERLAY,
          exit: () => {
            actions.deleteInteractions('CreatePunch');
            actions.deleteInteractions('PlacePunch');
          },
        },
      },
    },
    {
      actions: {
        [Actions.VIEW_OVERLAY]: (_, event) => {
          if (event.data?.interaction) {
            actions.createInteraction(event.data.interaction);
          }
        },
      },
    }
  );

export default createMapOverlayMachine;
